/**
 * Credit / Debit Notes page (Feb 2026).
 *
 * Single page handling all four note kinds via tabs:
 *   - Sales Credit Note   (issued to Customer)
 *   - Sales Debit Note    (issued to Customer)
 *   - Purchase Debit Note (issued to Supplier)
 *   - Purchase Credit Note (received from Supplier)
 *
 * Backend contract: `/api/credit-debit-notes` (see credit_debit_notes_routes.py).
 * Statement of Account picks up approved notes automatically.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, FileText, Check, Trash2, Download, Info } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_KIND_META = {
  sales_cn: {
    label: "Sales Credit Note",
    short: "Credit Note",
    party: "customer",
    partyLabel: "Customer",
    contactCategory: "customer",
    invoiceKind: "sales",
    description: "Issued to customer. Reduces AR (returns, discount, VAT correction).",
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
  },
  sales_dn: {
    label: "Sales Debit Note",
    short: "Debit Note",
    party: "customer",
    partyLabel: "Customer",
    contactCategory: "customer",
    invoiceKind: "sales",
    description: "Issued to customer. Adds to AR (extra charge, under-billing fix).",
    color: "bg-amber-100 text-amber-800 border-amber-200",
  },
  purchase_dn: {
    label: "Purchase Debit Note",
    short: "Purchase DN",
    party: "supplier",
    partyLabel: "Supplier",
    contactCategory: "supplier",
    invoiceKind: "purchase",
    description: "Issued to supplier. Reduces AP (returns to supplier, price claim).",
    color: "bg-sky-100 text-sky-800 border-sky-200",
  },
  purchase_cn: {
    label: "Purchase Credit Note",
    short: "Purchase CN",
    party: "supplier",
    partyLabel: "Supplier",
    contactCategory: "supplier",
    invoiceKind: "purchase",
    description: "Received from supplier. Reduces AP (rebate, damaged goods credit).",
    color: "bg-purple-100 text-purple-800 border-purple-200",
  },
};

const emptyLine = () => ({
  description: "",
  quantity: 1,
  unit_price: 0,
  tax_rate: 5,
  unit: "",
});

const emptyForm = (kind) => ({
  note_kind: kind,
  party_id: "",
  date: new Date().toISOString().split("T")[0],
  linked_invoice_id: "",
  linked_invoice_number: "",
  reason: "",
  reason_notes: "",
  lines: [emptyLine()],
  apply_to_invoice: true,
  vat_category: "standard",
  is_vat_inclusive: false,
  notes: "",
  // "Lump-sum" mode: single description + total amount. Skips the line-items
  // table entirely — useful for goodwill credits, VAT-only adjustments, and
  // rebates where the customer / supplier doesn't need a per-item breakdown.
  is_lump_sum: false,
  lump_sum_description: "",
  lump_sum_amount: 0,
});

export default function CreditDebitNotesPage() {
  const [activeKind, setActiveKind] = useState("sales_cn");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm("sales_cn"));
  const [saving, setSaving] = useState(false);

  const meta = NOTE_KIND_META[activeKind];

  const loadNotes = async (kind) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/credit-debit-notes?note_kind=${kind}`);
      setNotes(res.data || []);
    } catch (e) {
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes(activeKind);
  }, [activeKind]);

  useEffect(() => {
    // one-time: reasons + contacts + invoices
    axios.get(`${API_URL}/credit-debit-notes/reasons`).then((r) => setReasons(r.data.reasons || []));
    axios.get(`${API_URL}/contacts`).then((r) => setContacts(r.data || [])).catch(() => setContacts([]));
    axios.get(`${API_URL}/invoices`).then((r) => setInvoices(r.data || [])).catch(() => setInvoices([]));
    axios.get(`${API_URL}/purchase-invoices`).then((r) => setPurchaseInvoices(r.data || [])).catch(() => setPurchaseInvoices([]));
  }, []);

  const filteredContacts = useMemo(() => {
    // Contacts use `contact_type` field with values customer / supplier / both.
    // Fall back to `category` for legacy records and always let "both" match.
    const wanted = meta.contactCategory; // "customer" or "supplier"
    return contacts.filter((c) => {
      const t = (c.contact_type || c.category || "").toLowerCase();
      return t === wanted || t === "both" || t === "";
    });
  }, [contacts, meta.contactCategory]);

  const filteredInvoices = useMemo(() => {
    if (!form.party_id) return [];
    const src = meta.invoiceKind === "purchase" ? purchaseInvoices : invoices;
    const partyKey = meta.invoiceKind === "purchase" ? "supplier_id" : "customer_id";
    return src.filter((inv) => inv[partyKey] === form.party_id);
  }, [form.party_id, invoices, purchaseInvoices, meta.invoiceKind]);

  const openCreateDialog = () => {
    setForm(emptyForm(activeKind));
    setDialogOpen(true);
  };

  const updateLine = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((ln, i) =>
        i === idx
          ? { ...ln, [field]: ["quantity", "unit_price", "tax_rate"].includes(field) ? (parseFloat(value) || 0) : value }
          : ln
      ),
    }));
  };

  const addLine = () => setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (idx) =>
    setForm((p) => ({ ...p, lines: p.lines.length > 1 ? p.lines.filter((_, i) => i !== idx) : p.lines }));

  const totals = useMemo(() => {
    // Lump-sum mode short-circuits the line-items math — treat as a single
    // "no-tax" adjustment. Users are expected to type the final net figure.
    if (form.is_lump_sum) {
      const amt = parseFloat(form.lump_sum_amount) || 0;
      return { subtotal: +amt.toFixed(2), tax: 0, total: +amt.toFixed(2) };
    }
    let subtotal = 0, tax = 0;
    const inclusive = form.is_vat_inclusive && form.vat_category === "standard";
    const forceZero = form.vat_category && form.vat_category !== "standard";
    form.lines.forEach((ln) => {
      const qty = parseFloat(ln.quantity) || 0;
      const price = parseFloat(ln.unit_price) || 0;
      const rate = (forceZero ? 0 : (parseFloat(ln.tax_rate) || 0)) / 100;
      const base = qty * price;
      if (inclusive && rate > 0) {
        const net = base / (1 + rate);
        subtotal += net;
        tax += base - net;
      } else {
        subtotal += base;
        tax += base * rate;
      }
    });
    return { subtotal: +subtotal.toFixed(2), tax: +tax.toFixed(2), total: +(subtotal + tax).toFixed(2) };
  }, [form.lines, form.vat_category, form.is_vat_inclusive, form.is_lump_sum, form.lump_sum_amount]);

  const onSelectInvoice = (invoiceId) => {
    const src = meta.invoiceKind === "purchase" ? purchaseInvoices : invoices;
    const inv = src.find((i) => i.id === invoiceId);
    setForm((p) => ({
      ...p,
      linked_invoice_id: invoiceId,
      linked_invoice_number: inv?.document_number || inv?.invoice_number || "",
      // Inherit VAT category + rates from source (user can override afterward)
      vat_category: inv?.vat_category || p.vat_category,
      is_vat_inclusive: !!inv?.is_vat_inclusive,
      lines: (inv?.lines || []).length
        ? inv.lines.map((ln) => ({
            description: ln.description || "",
            quantity: ln.quantity ?? 1,
            unit_price: ln.unit_price ?? 0,
            tax_rate: ln.tax_rate ?? 5,
            unit: ln.unit || "",
          }))
        : p.lines,
    }));
  };

  const handleCreate = async () => {
    if (!form.party_id) { toast.error(`${meta.partyLabel} is required`); return; }
    if (!form.reason) { toast.error("Reason is required (UAE VAT / GST compliance)"); return; }

    let payload;
    if (form.is_lump_sum) {
      const amt = parseFloat(form.lump_sum_amount) || 0;
      if (amt <= 0) { toast.error("Lump-sum amount must be greater than 0"); return; }
      if (!(form.lump_sum_description || "").trim()) { toast.error("Lump-sum description is required"); return; }
      // Encode the lump-sum as a single line: qty=1, price=amount, tax=0.
      // The backend already computes totals from lines, so this keeps the
      // storage schema consistent while giving accountants a description-only
      // credit / debit adjustment on the PDF and Statement of Account.
      payload = {
        ...form,
        lines: [{ description: form.lump_sum_description, quantity: 1, unit_price: amt, tax_rate: 0 }],
        vat_category: "out_of_scope",
        is_vat_inclusive: false,
      };
      delete payload.is_lump_sum;
      delete payload.lump_sum_description;
      delete payload.lump_sum_amount;
    } else {
      if (!form.lines.length || !form.lines.some((l) => (l.description || "").trim())) {
        toast.error("At least one line item is required");
        return;
      }
      payload = { ...form };
      delete payload.is_lump_sum;
      delete payload.lump_sum_description;
      delete payload.lump_sum_amount;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/credit-debit-notes`, payload);
      toast.success(`${meta.short} created (draft)`);
      setDialogOpen(false);
      loadNotes(activeKind);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to create ${meta.short}`);
    } finally {
      setSaving(false);
    }
  };

  const approveNote = async (id) => {
    try {
      await axios.post(`${API_URL}/credit-debit-notes/${id}/approve`);
      toast.success("Note approved & journal entry posted");
      loadNotes(activeKind);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };

  const deleteNote = async (id, docNumber) => {
    if (!window.confirm(`Delete ${docNumber}? If approved, its journal entry will be voided.`)) return;
    try {
      await axios.delete(`${API_URL}/credit-debit-notes/${id}`);
      toast.success(`${docNumber} deleted`);
      loadNotes(activeKind);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const downloadPdf = async (id, docNumber) => {
    try {
      const res = await axios.get(`${API_URL}/credit-debit-notes/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Credit &amp; Debit Notes</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Post-invoice adjustments — flows automatically into the Statement of Account and Journal.
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="new-note-btn">
          <Plus size={16} className="mr-2" /> New {meta.short}
        </Button>
      </div>

      <Tabs value={activeKind} onValueChange={setActiveKind}>
        <TabsList className="grid grid-cols-4 w-full max-w-3xl">
          {Object.entries(NOTE_KIND_META).map(([k, m]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>
              {m.short}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(NOTE_KIND_META).map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            <Card>
              <CardHeader className="flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-lg">{NOTE_KIND_META[k].label}</CardTitle>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Info size={12} /> {NOTE_KIND_META[k].description}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">Loading…</div>
                ) : notes.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">
                    No {NOTE_KIND_META[k].short}s yet. Click <b>New {NOTE_KIND_META[k].short}</b> to create one.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Doc #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>{NOTE_KIND_META[k].partyLabel}</TableHead>
                        <TableHead>Linked Invoice</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notes.map((n) => (
                        <TableRow key={n.id} data-testid={`note-row-${n.document_number}`}>
                          <TableCell className="font-mono text-xs">{n.document_number}</TableCell>
                          <TableCell className="text-xs">{n.date}</TableCell>
                          <TableCell className="text-sm">{n.party_name}</TableCell>
                          <TableCell className="text-xs font-mono">{n.linked_invoice_number || "—"}</TableCell>
                          <TableCell className="text-xs">{n.reason}</TableCell>
                          <TableCell className="text-right font-mono">{Number(n.total || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={n.status === "approved" ? NOTE_KIND_META[k].color : ""}>
                              {n.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            {n.status === "draft" && (
                              <Button size="sm" variant="outline" onClick={() => approveNote(n.id)} data-testid={`approve-${n.document_number}`}>
                                <Check size={14} className="mr-1" /> Approve
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(n.id, n.document_number)} data-testid={`pdf-${n.document_number}`}>
                              <Download size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteNote(n.id, n.document_number)} data-testid={`delete-${n.document_number}`}>
                              <Trash2 size={14} className="text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} /> New {meta.label}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Party + Date + Invoice */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{meta.partyLabel} *</Label>
                <Select value={form.party_id} onValueChange={(v) => setForm((p) => ({ ...p, party_id: v, linked_invoice_id: "", linked_invoice_number: "" }))}>
                  <SelectTrigger data-testid="party-select">
                    <SelectValue placeholder={`Pick a ${meta.partyLabel.toLowerCase()}…`} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredContacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="date-input" />
              </div>
              <div>
                <Label>Linked Invoice (optional)</Label>
                <Select value={form.linked_invoice_id} onValueChange={onSelectInvoice} disabled={!form.party_id}>
                  <SelectTrigger data-testid="invoice-select">
                    <SelectValue placeholder={form.party_id ? "Optional — inherits lines & VAT" : "Pick party first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredInvoices.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        No invoices found for this {meta.partyLabel.toLowerCase()}
                      </div>
                    )}
                    {filteredInvoices.map((inv) => {
                      const num = inv.document_number || inv.invoice_number || inv.id?.slice(0, 8);
                      const total = Number(inv.final_payable ?? inv.total ?? 0).toFixed(2);
                      const dt = (inv.date || "").slice(0, 10);
                      return (
                        <SelectItem key={inv.id} value={inv.id}>
                          <span className="font-mono text-xs">{num}</span>
                          <span className="text-muted-foreground mx-2">·</span>
                          <span className="text-xs">{dt}</span>
                          <span className="text-muted-foreground mx-2">·</span>
                          <span className="font-mono">{total}</span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Reason (mandatory for UAE VAT / GST) */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label>Reason * <span className="text-xs text-muted-foreground">(VAT / GST compliance)</span></Label>
                <Select value={form.reason} onValueChange={(v) => setForm((p) => ({ ...p, reason: v }))}>
                  <SelectTrigger data-testid="reason-select">
                    <SelectValue placeholder="Pick a reason…" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Reason Notes (optional)</Label>
                <Input value={form.reason_notes || ""} onChange={(e) => setForm((p) => ({ ...p, reason_notes: e.target.value }))} placeholder="Free-text explanation shown on PDF" data-testid="reason-notes-input" />
              </div>
            </div>

            {/* Apply to invoice toggle */}
            <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/30">
              <input
                type="checkbox"
                id="apply-to-invoice"
                checked={form.apply_to_invoice}
                onChange={(e) => setForm((p) => ({ ...p, apply_to_invoice: e.target.checked }))}
                data-testid="apply-toggle"
              />
              <Label htmlFor="apply-to-invoice" className="cursor-pointer flex-1">
                <div className="font-medium">Apply to linked invoice</div>
                <div className="text-xs text-muted-foreground">
                  {form.apply_to_invoice
                    ? `Will auto-adjust the linked invoice's balance on approval.`
                    : `Standalone credit — posts a journal entry only; source invoice untouched.`}
                </div>
              </Label>
            </div>

            {/* VAT category */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>VAT Category</Label>
                <Select value={form.vat_category} onValueChange={(v) => setForm((p) => ({ ...p, vat_category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Rated</SelectItem>
                    <SelectItem value="zero_rated">Zero Rated (0%)</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                    <SelectItem value="out_of_scope">Out of Scope</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_vat_inclusive} onChange={(e) => setForm((p) => ({ ...p, is_vat_inclusive: e.target.checked }))} />
                  VAT Inclusive rate
                </label>
              </div>
            </div>

            {/* Lump-sum toggle */}
            <div className="flex items-center gap-2 p-3 rounded-md border bg-blue-50/30 dark:bg-blue-950/20">
              <input
                type="checkbox"
                id="lump-sum"
                checked={form.is_lump_sum}
                onChange={(e) => setForm((p) => ({ ...p, is_lump_sum: e.target.checked }))}
                data-testid="lump-sum-toggle"
              />
              <Label htmlFor="lump-sum" className="cursor-pointer flex-1">
                <div className="font-medium">Lump-sum {meta.short.toLowerCase()} (no line items)</div>
                <div className="text-xs text-muted-foreground">
                  Free-text description + a single total amount — useful for goodwill credits, rebates, VAT-only adjustments.
                </div>
              </Label>
            </div>

            {/* Lines OR Lump-sum panel */}
            {form.is_lump_sum ? (
              <div className="space-y-3 p-4 border rounded-md bg-muted/20">
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={form.lump_sum_description}
                    onChange={(e) => setForm((p) => ({ ...p, lump_sum_description: e.target.value }))}
                    rows={2}
                    placeholder={`e.g. Goodwill credit for delayed delivery vs INV-${form.linked_invoice_number || "XXXX"}`}
                    data-testid="lump-sum-desc"
                  />
                </div>
                <div className="w-1/3">
                  <Label>Amount *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.lump_sum_amount}
                    onChange={(e) => setForm((p) => ({ ...p, lump_sum_amount: e.target.value }))}
                    className="font-mono text-right"
                    data-testid="lump-sum-amount"
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  Lump-sum notes are stored as a single line with the amount above, no VAT applied.
                </div>
              </div>
            ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                  <Plus size={14} className="mr-1" /> Add Line
                </Button>
              </div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs">
                    <tr>
                      <th className="text-left px-2 py-2 w-1/2">Description</th>
                      <th className="px-2 py-2">Qty</th>
                      <th className="px-2 py-2">Unit Price</th>
                      <th className="px-2 py-2">Tax %</th>
                      <th className="text-right px-2 py-2">Line Total</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((ln, i) => {
                      const base = (parseFloat(ln.quantity) || 0) * (parseFloat(ln.unit_price) || 0);
                      const tax = base * ((parseFloat(ln.tax_rate) || 0) / 100);
                      return (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">
                            <Input value={ln.description} onChange={(e) => updateLine(i, "description", e.target.value)} placeholder="Item description" data-testid={`line-${i}-desc`} />
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" min="0" step="0.01" value={ln.quantity ?? 0} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="text-right" data-testid={`line-${i}-qty`} />
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" min="0" step="0.01" value={ln.unit_price ?? 0} onChange={(e) => updateLine(i, "unit_price", e.target.value)} className="text-right" data-testid={`line-${i}-price`} />
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" min="0" max="100" step="0.01" value={ln.tax_rate ?? 0} onChange={(e) => updateLine(i, "tax_rate", e.target.value)} className="text-right" data-testid={`line-${i}-tax`} />
                          </td>
                          <td className="px-2 py-1 text-right font-mono">{(base + tax).toFixed(2)}</td>
                          <td className="px-2 py-1">
                            {form.lines.length > 1 && (
                              <button type="button" onClick={() => removeLine(i)} className="text-destructive text-lg leading-none">×</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{totals.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">VAT</span><span className="font-mono">{totals.tax.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span className="font-mono">{totals.total.toFixed(2)}</span></div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Internal Notes (optional)</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} data-testid="notes-input" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-note-btn">
              {saving ? "Saving…" : "Save as Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
